import { useState, useEffect } from 'react';
import { X, User, Mail, Phone, MapPin, Heart, Shield, Calendar, Hash, FileText } from 'lucide-react';
import { patientsApi, Patient } from '../../lib/api';
import MedicalHistoryModal from './MedicalHistoryModal';

interface PatientDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string | null;
}

const PatientDetailsModal = ({ isOpen, onClose, patientId }: PatientDetailsModalProps) => {
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMedicalHistory, setShowMedicalHistory] = useState(false);

  useEffect(() => {
    if (isOpen && patientId) {
      fetchPatientDetails();
    }
  }, [isOpen, patientId]);

  const fetchPatientDetails = async () => {
    if (!patientId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await patientsApi.getPatient(patientId);
      if (response.data.success && response.data.data) {
        setPatient(response.data.data);
      } else {
        setError('Failed to load patient details');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load patient details');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const formatGender = (gender: string) => {
    return gender.toLowerCase().charAt(0).toUpperCase() + gender.toLowerCase().slice(1);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-primary-50">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
              {patient ? (
                <span className="text-primary-600 font-semibold text-xl">
                  {patient.user.firstName[0]}{patient.user.lastName[0]}
                </span>
              ) : (
                <User className="h-8 w-8 text-primary-600" />
              )}
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                {patient ? `${patient.user.firstName} ${patient.user.lastName}` : 'Patient Details'}
              </h2>
              {patient && (
                <p className="text-primary-600 font-medium">#{patient.patientNumber}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="ml-3 text-gray-600">Loading patient details...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-500 mb-2">Error loading patient details</div>
              <p className="text-gray-600">{error}</p>
              <button 
                onClick={fetchPatientDetails}
                className="mt-4 btn-primary"
              >
                Try Again
              </button>
            </div>
          ) : patient ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Personal Information */}
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <User className="h-5 w-5 mr-2 text-primary-600" />
                    Personal Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Full Name:</span>
                      <span className="font-medium">{patient.user.firstName} {patient.user.lastName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date of Birth:</span>
                      <span className="font-medium">
                        {new Date(patient.dateOfBirth).toLocaleDateString()} 
                        <span className="text-gray-500 ml-2">(Age {calculateAge(patient.dateOfBirth)})</span>
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Gender:</span>
                      <span className="font-medium">{formatGender(patient.gender)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Registered:</span>
                      <span className="font-medium">{new Date(patient.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Phone className="h-5 w-5 mr-2 text-primary-600" />
                    Contact Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Email:</span>
                      <span className="font-medium">{patient.user.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Phone:</span>
                      <span className="font-medium">{patient.user.phone}</span>
                    </div>
                  </div>
                </div>

                {/* Medical Records Summary */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Heart className="h-5 w-5 mr-2 text-red-500" />
                    Medical Records Summary
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-white rounded-lg border">
                      <div className="text-2xl font-bold text-blue-600">
                        {patient._count?.appointments || 0}
                      </div>
                      <div className="text-sm text-gray-600">Appointments</div>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg border">
                      <div className="text-2xl font-bold text-green-600">
                        {patient._count?.medicalHistory || 0}
                      </div>
                      <div className="text-sm text-gray-600">Medical Records</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address & Emergency Contact */}
              <div className="space-y-6">
                {/* Address */}
                {patient.address && (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <MapPin className="h-5 w-5 mr-2 text-primary-600" />
                      Address
                    </h3>
                    <div className="space-y-2">
                      <div>{patient.address.street}</div>
                      <div>{patient.address.city}, {patient.address.state} {patient.address.zipCode}</div>
                      <div>{patient.address.country}</div>
                    </div>
                  </div>
                )}

                {/* Emergency Contact */}
                {patient.emergencyContact && (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Heart className="h-5 w-5 mr-2 text-red-500" />
                      Emergency Contact
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Name:</span>
                        <span className="font-medium">{patient.emergencyContact.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Relationship:</span>
                        <span className="font-medium">{patient.emergencyContact.relationship}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Phone:</span>
                        <span className="font-medium">{patient.emergencyContact.phone}</span>
                      </div>
                      {patient.emergencyContact.email && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Email:</span>
                          <span className="font-medium">{patient.emergencyContact.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Insurance Information */}
                {patient.insuranceInfo && (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Shield className="h-5 w-5 mr-2 text-blue-600" />
                      Insurance Information
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Provider:</span>
                        <span className="font-medium">{patient.insuranceInfo.provider}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Policy Number:</span>
                        <span className="font-medium">{patient.insuranceInfo.policyNumber}</span>
                      </div>
                      {patient.insuranceInfo.groupNumber && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Group Number:</span>
                          <span className="font-medium">{patient.insuranceInfo.groupNumber}</span>
                        </div>
                      )}
                      {patient.insuranceInfo.expiryDate && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Expiry Date:</span>
                          <span className="font-medium">
                            {new Date(patient.insuranceInfo.expiryDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <button
            onClick={() => setShowMedicalHistory(true)}
            className="btn-primary"
          >
            <FileText className="h-4 w-4 mr-2" />
            View Medical History
          </button>
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            Close
          </button>
        </div>

        {/* Medical History Modal */}
        <MedicalHistoryModal
          isOpen={showMedicalHistory}
          onClose={() => setShowMedicalHistory(false)}
          patient={patient}
        />
      </div>
    </div>
  );
};

export default PatientDetailsModal; 